/**
 * The trailing score beside the range score: same badge, same hover breakdown, on the
 * last TRAILING_MONTHS closed months ending at the range's end (measures/trailing.ts).
 * One component so the Scorecard, Employees and the person page cannot word it apart.
 */
import { useAppData } from '@/data/AppData'
import { monthSpan } from '@/format'
import { formatPoints, scoreBand, type Scorecard } from '@/measures/scorecard'
import { TRAILING_LABEL, TRAILING_MONTHS } from '@/measures/trailing'
import { ScoreTip } from './ScoreTip'
import { Unscored } from './Bits'

/** "Trailing 3-mo · Jun–Aug 2026", or what the window actually is when it is short. */
export function useTrailingCaption(): { label: string; span: string; note: string } {
  const { trailing } = useAppData()
  const span = monthSpan(trailing.months)
  const note = trailing.months.length === 0
    ? 'No closed month on or before the range’s end, so no trailing score.'
    : `Scored on ${span} pooled — the last ${TRAILING_MONTHS} closed months ending at the range’s end, against peers active in them. A month still running is never included.${
        trailing.complete
          ? ''
          : ` Only ${trailing.months.length} closed ${trailing.months.length === 1 ? 'month' : 'months'} in the data.`
      }`
  return { label: TRAILING_LABEL, span, note }
}

/** The trailing scorecard for one person, or undefined when they are not in the window. */
export function useTrailingScore(name: string): Scorecard | undefined {
  return useAppData().trailing.byName.get(name)
}

/** The trailing score for one person, as a badge with the breakdown on hover. */
export function TrailingBadge({ score, who }: { score: Scorecard | undefined; who: string }) {
  if (!score)
    return (
      <span className="sub2" title="No rows in the trailing months.">
        —
      </span>
    )
  return (
    <ScoreTip score={score} who={who}>
      {score.scored ? (
        <span className={`scbadge ${scoreBand(score.total)}`}>{formatPoints(score.total)}</span>
      ) : (
        <Unscored score={score} />
      )}
    </ScoreTip>
  )
}
